import { useState, useEffect } from 'react'
import { supabase } from '../services/supabase'
import { useAuth } from '../contexts/AuthContext'

interface TrialStatus {
    hasActiveTrial: boolean
    hasAnySubscription: boolean
    subscriptionStatus: 'sem_assinatura' | 'trial_ativo' | 'assinatura_ativa' | 'assinatura_cancelada' | 'assinatura_expirada' | 'outro_status'
    trialInfo: {
        id: string
        plano_nome: string
        plano_display: string
        data_fim_trial: string | null
        dias_restantes: number | null
    } | null
    currentPlan: {
        id: string
        plano_id: string
        plano_nome: string
        plano_display: string
        status: string
    } | null
    loading: boolean
    error: string | null
}

export const useTrialStatus = (): TrialStatus => {
    const { user } = useAuth()
    const [trialStatus, setTrialStatus] = useState<TrialStatus>({
        hasActiveTrial: false,
        hasAnySubscription: false,
        subscriptionStatus: 'sem_assinatura',
        trialInfo: null,
        currentPlan: null,
        loading: true,
        error: null
    })

    useEffect(() => {
        const checkTrialStatus = async () => {
                if (!user) {
                setTrialStatus({
                    hasActiveTrial: false,
                    hasAnySubscription: false,
                    subscriptionStatus: 'sem_assinatura',
                    trialInfo: null,
                    currentPlan: null,
                    loading: false,
                    error: null
                })
                return
            }

            try {
                setTrialStatus(prev => ({ ...prev, loading: true, error: null }))
                console.log('🔍 DEBUG: Verificando trial status para user:', user.id)

                // Buscar assinaturas do usuário diretamente
                const { data: assinaturasData, error: assinaturasError } = await supabase
                    .from('assinaturas')
                    .select(`
                        id,
                        user_id,
                        status,
                        data_fim_trial,
                        plano_id,
                        planos!inner (
                            nome,
                            nome_display
                        )
                    `)
                    .eq('user_id', user.id)
                    .order('created_at', { ascending: false })

                console.log('🔍 DEBUG: Assinaturas encontradas:', { assinaturasData, assinaturasError })

                if (assinaturasError) {
                    throw assinaturasError
                }

                // Processar dados das assinaturas
                const hasAnySubscription = assinaturasData && assinaturasData.length > 0
                const ultimaAssinatura = assinaturasData && assinaturasData.length > 0 ? assinaturasData[0] : null

                // Determinar status da assinatura
                let subscriptionStatus = 'sem_assinatura'
                if (ultimaAssinatura) {
                    if (ultimaAssinatura.status === 'trial' &&
                        (ultimaAssinatura.data_fim_trial === null || new Date(ultimaAssinatura.data_fim_trial) > new Date())) {
                        subscriptionStatus = 'trial_ativo'
                    } else if (ultimaAssinatura.status === 'ativa') {
                        subscriptionStatus = 'assinatura_ativa'
                    } else if (ultimaAssinatura.status === 'cancelada') {
                        subscriptionStatus = 'assinatura_cancelada'
                    } else if (ultimaAssinatura.status === 'expirada') {
                        subscriptionStatus = 'assinatura_expirada'
                    } else {
                        subscriptionStatus = 'outro_status'
                    }
                }

                const hasActiveTrial = subscriptionStatus === 'trial_ativo'

                let trialInfo = null
                if (hasActiveTrial && ultimaAssinatura) {
                    // Calcular dias restantes
                    let diasRestantes = null
                    if (ultimaAssinatura.data_fim_trial) {
                        const agora = new Date()
                        const fimTrial = new Date(ultimaAssinatura.data_fim_trial)
                        const diffTime = fimTrial.getTime() - agora.getTime()
                        diasRestantes = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
                    }

                    trialInfo = {
                        id: ultimaAssinatura.id,
                        plano_nome: (ultimaAssinatura.planos as any).nome,
                        plano_display: (ultimaAssinatura.planos as any).nome_display,
                        data_fim_trial: ultimaAssinatura.data_fim_trial,
                        dias_restantes: diasRestantes
                    }
                }

                // Informação do plano atual (mesmo se expirado)
                let currentPlan = null
                if (ultimaAssinatura) {
                    currentPlan = {
                        id: ultimaAssinatura.id,
                        plano_id: ultimaAssinatura.plano_id,
                        plano_nome: (ultimaAssinatura.planos as any).nome,
                        plano_display: (ultimaAssinatura.planos as any).nome_display,
                        status: ultimaAssinatura.status
                    }
                }

                console.log('🔍 DEBUG: Status final:', {
                    hasAnySubscription,
                    subscriptionStatus,
                    hasActiveTrial,
                    trialInfo,
                    currentPlan
                })

                    setTrialStatus({
                        hasActiveTrial,
                        hasAnySubscription,
                        subscriptionStatus: subscriptionStatus as 'sem_assinatura' | 'trial_ativo' | 'assinatura_ativa' | 'assinatura_cancelada' | 'assinatura_expirada' | 'outro_status',
                        trialInfo,
                        currentPlan,
                        loading: false,
                        error: null
                    })
            } catch (err) {
                console.error('Erro ao verificar status do trial:', err)
                setTrialStatus({
                    hasActiveTrial: false,
                    hasAnySubscription: false,
                    subscriptionStatus: 'sem_assinatura',
                    trialInfo: null,
                    currentPlan: null,
                    loading: false,
                    error: err instanceof Error ? err.message : 'Erro ao verificar trial'
                })
            }
        }

        checkTrialStatus()

        // Escutar mudanças na autenticação
        const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                checkTrialStatus()
            } else {
                setTrialStatus({
                    hasActiveTrial: false,
                    hasAnySubscription: false,
                    subscriptionStatus: 'sem_assinatura',
                    trialInfo: null,
                    currentPlan: null,
                    loading: false,
                    error: null
                })
            }
        })

        return () => {
            authListener?.subscription.unsubscribe()
        }
    }, [user])

    return trialStatus
}
